import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

/**
 * Navegación del onboarding: un punto por paso, con flechas a los costados.
 *
 * Sin texto: el bloque entero va centrado y los puntos muestran de un vistazo
 * cuántos pasos hay y en cuál estás, que la barra de progreso sola no dice.
 *
 * Los puntos ya recorridos son clickeables para volver; los que faltan, no:
 * saltear hacia adelante se saltearía las validaciones de cada paso.
 *
 * Está armado sobre el <Button> de la app, así que toma el color de marca y la
 * forma de pastilla sin repetir nada.
 */
const PasoNavegador = ({
  pasos,
  pasoActual,
  onAnterior,
  onSiguiente,
  onIrAPaso,
  puedeAvanzar = true,
  mostrarSiguiente = true,
  textoSiguiente = 'Siguiente',
  motivoBloqueo = '',
  deshabilitado = false,
}) => {
  const indiceActual = pasos.indexOf(pasoActual);
  const hayAnterior = indiceActual > 0;

  // El aire de arriba sigue el mismo criterio que las tarjetas del paso: sale de
  // la altura de la ventana y no de un valor fijo. Con `pt-8` clavado, en una
  // pantalla baja los botones quedaban demasiado lejos del contenido.
  return (
    <nav
      role="navigation"
      aria-label="Progreso de la configuración"
      className="mx-auto flex w-full items-center justify-center gap-3 pt-[clamp(0.5rem,2vh,2rem)] sm:gap-4"
    >
      {/* Solo la flecha. El aria-label y el title se quedan: sin texto visible,
          son lo único que dice qué hace el botón, tanto para un lector de
          pantalla como para quien duda al pasar el mouse. */}
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={onAnterior}
        disabled={!hayAnterior || deshabilitado}
        aria-label="Volver al paso anterior"
        title="Atrás"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden="true" />
      </Button>

      <ol className="flex flex-row items-center gap-1.5">
        {pasos.map((paso, i) => {
          const esActual = i === indiceActual;
          const yaPasado = i < indiceActual;

          return (
            <li key={paso}>
              <button
                type="button"
                onClick={() => yaPasado && onIrAPaso(paso)}
                disabled={!yaPasado || deshabilitado}
                aria-current={esActual ? 'step' : undefined}
                aria-label={
                  esActual
                    ? `Paso ${i + 1} de ${pasos.length}, el actual`
                    : `Ir al paso ${i + 1} de ${pasos.length}`
                }
                className={cn(
                  'block h-2 rounded-full transition-all duration-300',
                  // El actual es más ancho en vez de más grande: la fila no se
                  // mueve cuando cambia de paso.
                  esActual ? 'w-6 bg-primary' : 'w-2',
                  yaPasado && 'cursor-pointer bg-primary/40 hover:bg-primary/70',
                  !esActual && !yaPasado && 'cursor-default bg-neutral-300'
                )}
              />
            </li>
          );
        })}
      </ol>

      {mostrarSiguiente ? (
        <Button
          type="button"
          size="icon"
          onClick={onSiguiente}
          disabled={!puedeAvanzar || deshabilitado}
          aria-label={textoSiguiente}
          title={!puedeAvanzar ? motivoBloqueo : textoSiguiente}
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </Button>
      ) : (
        // Hueco del mismo tamaño que el botón: sin esto, los puntos se corren
        // del centro cuando el botón desaparece en el último paso.
        <div className="h-10 w-10" aria-hidden="true" />
      )}
    </nav>
  );
};

export default PasoNavegador;
